import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { z } from 'zod';
import { useIntercomDispatcher } from '@/hooks/comm/use-comm';
import { systemSettingQueryOptions } from '@/hooks/files/use-system-setting';
import { responseScheme } from '@/models/response';
import { UserType, userInfoSchema } from '@/models/user/user-info';
import { useAuthStore } from '@/stores/auth';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';

export const userInfoQueryOptions = queryOptions({
  queryKey: ['/GetUserInfo'],
  queryFn: async ({ signal }) => {
    const response = await fetcher<UserInfoResponseType>('/GetUserInfo', undefined, {
      signal,
      schema: userInfoResponseSchema,
      validResponseCodes: [0],
    });

    return response.UserInfo ?? null;
  },
});

export default function useUserInfo() {
  const { dispatch } = useIntercomDispatcher();
  const query = useQuery(userInfoQueryOptions);

  useEffect(() => {
    if (query.data) {
      useAuthStore.getState().updateAuth({
        NickName: query.data.NickName,
        AvatarUrl: query.data.HeadImg,
      });
      dispatch({ event: 'update-auth-credentials' });
    }
  }, [query.data, dispatch]);

  return query;
}

export function useIsInternalUser() {
  return useQueries({
    queries: [systemSettingQueryOptions, userInfoQueryOptions],
    combine: ([systemSetting, userInfo]) => {
      if (systemSetting.status !== 'success' || userInfo.status !== 'success') {
        return {
          isInternalUser: false,
          isControlled: true,
          userType: UserType.User,
        };
      }

      return {
        isInternalUser: systemSetting.data.enableInternalUserControl ? userInfo.data.Type === UserType.Admin : true,
        isControlled: systemSetting.data.enableInternalUserControl,
        userType: userInfo.data.Type,
      };
    },
  });
}

const userInfoResponseSchema = responseScheme.extend({
  UserInfo: userInfoSchema,
});

type UserInfoResponseType = z.infer<typeof userInfoResponseSchema>;
